import React from "react";
import { heroData } from "../data/mock";
import { Phone, MessageCircle, MapPin, Star } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const handleCall = () => {
    window.open(`tel:${heroData.phone}`, "_self");
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "Hello! I am looking for a taxi service in Udaipur. Please share availability and pricing."
    );
    window.open(`https://wa.me/${heroData.whatsapp}?text=${message}`, "_blank");
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* BACKGROUND IMAGE */}
      <img
        src={heroData.backgroundImage || "/images/bahubali.jpg"}
        alt="Bahubali Cabs Udaipur"
        fetchpriority="high"
        className="absolute inset-0 w-full h-full object-cover scale-105 select-none pointer-events-none"
      />

      {/* DARK OVERLAY */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-900/70 to-slate-900/95" />

      {/* GLOWS */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-amber-400/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-40 right-10 w-80 h-80 bg-amber-500/20 rounded-full blur-[120px]" />

      {/* CONTENT */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
        {/* LOCATION + RATING */}
        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2.5 mb-8 shadow-xl">
          <MapPin className="w-5 h-5 text-amber-400" />
          <span className="text-white text-sm sm:text-base md:text-lg font-bold">
            Udaipur, Rajasthan
          </span>
          <div className="flex items-center gap-1 ml-2 border-l border-white/20 pl-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>

        {/* MAIN HEADING */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black mb-6 leading-[1.1]">
          <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">
            Udaipur Taxi Service
          </span>
        </h1>

        {/* SUB HEADING */}
        <p className="text-lg sm:text-xl md:text-2xl text-white/95 mb-8 font-medium">
          Reliable Local, Airport & Outstation Cabs by{" "}
          <span className="font-extrabold text-amber-400 underline underline-offset-8 decoration-amber-500/30">
            Bahubali Cabs
          </span>
        </p>

        {/* DESCRIPTION CARD */}
        <div className="max-w-4xl mx-auto bg-slate-900/40 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-6 sm:p-8 md:p-12 mb-12 shadow-2xl">
          <p className="text-white/90 text-base sm:text-lg md:text-xl leading-relaxed mb-8">
            Experience premium travel across the City of Lakes with our
            highly-rated drivers. We specialize in{" "}
            <strong>sightseeing tours</strong>,{" "}
            <strong>airport transfers</strong>, and{" "}
            <strong>outstation journeys</strong> with transparent pricing.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Local Sightseeing",
              "Airport Transfers",
              "Outstation Taxi",
              "Rajasthan Tours",
            ].map((tag) => (
              <span
                key={tag}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-amber-300 text-xs sm:text-sm font-bold uppercase tracking-widest"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* CTA BUTTONS */}
        <div className="flex flex-col sm:flex-row justify-center gap-5">
          <Button
            onClick={handleCall}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-lg sm:text-xl px-8 sm:px-10 py-6 sm:py-8 rounded-2xl shadow-[0_20px_50px_rgba(245,158,11,0.3)] transition-all hover:scale-105 active:scale-95 min-w-[240px]"
          >
            <Phone className="w-5 h-5 mr-3 fill-current" />
            Call Now
          </Button>

          <Button
            onClick={handleWhatsApp}
            variant="outline"
            className="bg-white/5 border-2 border-white/20 text-white font-bold text-lg sm:text-xl px-8 sm:px-10 py-6 sm:py-8 rounded-2xl backdrop-blur-md hover:bg-white/10 transition-all hover:scale-105 active:scale-95 min-w-[240px]"
          >
            <MessageCircle className="w-5 h-5 mr-3 text-green-400" />
            WhatsApp Booking
          </Button>
        </div>

        {/* TRUST LINE */}
        <p className="mt-10 text-sm sm:text-base md:text-lg text-white/50 font-medium">
          🏆 #1 Rated Cab Service in Udaipur • Trusted by 10,000+ Tourists
        </p>
      </div>
      
    </section>
    
  );
};

export default HeroSection;
